import {
  ChangeEvent,
  Dispatch,
  SetStateAction,
  useEffect,
  useMemo,
  useState,
} from 'react';

import {
  ChakraBox,
  ChakraText,
  DateFormat,
  EquipUIFireflyV2Theme,
  EquipUITheme,
} from '@equip.health/ui';
import { sortBy } from 'lodash';
import { useHistory, useParams } from 'react-router-dom';

import { OverviewPageEvents } from '~/analytics/overviewPageEvents';
import { PatientEvents } from '~/analytics/patientEvents';
import { PAGE_LAYOUT } from '~/lib/constants';
import { AnalyticsSourceValues } from '~/lib/constants/AnalyticsSource.constants';
import { PatientTabs } from '~/lib/constants/patients.constants';
import { PatientTabsCollection } from '~/lib/hooks/usePatientTabsCollection';
import { MaudPermissions } from '~/lib/constants/permissions.constants';
import { useAnalytics } from '~/lib/contexts/AnalyticsContext';
import { useGlobalContext } from '~/lib/contexts/GlobalContext';
import { useTaskContext } from '~/lib/contexts/TaskContext';
import useUnsavedChangesGuard from '~/lib/hooks/useUnsavedChangesGuard';
import usePath from '~/lib/hooks/usePath';
import { getAgeFromDOB } from '~/lib/util/patient.util';
import {
  TaskStatusLabel,
  TaskStatusValue,
  filterDisabledSubCategories,
  filterInActiveSubCategories,
  getVoidReasonData,
  isOpenTask,
  isValidName,
  mapCompServTaskToTask,
  taskStatusDefinitions,
} from '~/lib/util/task.util';
import Loading from '~/pages/loading';
import { Path } from '~/routes/constants';
import { TasksSubTab } from '~/pages/maud/patients/[patientId]/tasks/constants';
import { useGetTaskVoidReasonsEnumQuery } from '~/store/services/enumerations';

import ActionsSection from './ActionsSection';
import ActivitySection from './ActivitySection';
import DetailsSection from './DetailsSection';
import EditTaskConfirmDrawer from './EditTaskConfirmDrawer';
import HeaderSection from './HeaderSection';
import TaskNotFound from './TaskNotFound';
import TitleSection from './TitleSection';
import ConfirmModal from '~/components/common/ConfirmModal';
import VoidOrCloseTaskModal from './VoidOrCloseTaskModal';
import AssigneeSelection from '../AssigneeSelection';
import FamilyAssigneeSelection from '../FamilyAssigneeSelection';
import PatientSelection from '../PatientSelection';
import { DateTime } from 'luxon';
import { useGetAppUsersQuery } from '~/store/services/appUsers';
import { useLazyGetTasksQuery } from '~/store/services/task';
import { QueryStatus } from '@reduxjs/toolkit/query';
import { useDatadog } from '~/lib/hooks/useDatadog';

const { neutral } = EquipUIFireflyV2Theme.colors;
const { labelBold, paragraphBold } = EquipUITheme.typography;

type TaskDetailProps = {
  taskId: string;
};

const TOP_LEVEL_ID = 'edit-task';

const TaskDetail = ({ taskId }: TaskDetailProps) => {
  const { permissions } = useGlobalContext();
  const { logError } = useDatadog();
  const hasEditFamilyTaskPermission =
    permissions[MaudPermissions.MAUD_EDIT_FAMILY_TASK];

  const [selectedTask, setSelectedTask] = useState<Task>(null);

  const [isVoidTaskModalOpen, setIsVoidTaskModalOpen] =
    useState<boolean>(false);
  const [isChangeVoidReasonModalOpen, setIsChangeVoidReasonModalOpen] =
    useState<boolean>(false);
  const [isCompleteTaskModalOpen, setIsCompleteTaskModalOpen] =
    useState<boolean>(false);
  const [newStatusPlaceholder, setNewStatusPlaceholder] =
    useState<TaskStatusValue>(null);

  const [originalTask, setOriginalTask] = useState<Task | null>(null);

  const [assignee, setAssignee] = useState<GetProviderByIdResponse | null>(
    null,
  );
  const [selectedFamilyTaskAssignees, setSelectedFamilyTaskAssignees] =
    useState<FamilyTaskAssignee[] | null>();

  const [category, setCategory] = useState<Category | null>(null);
  const [description, setDescription] = useState<string | null>(null);
  const [dueDate, setDueDate] = useState<string | null>(null);
  const [name, setName] = useState<string | null>(null);
  const [patient, setPatient] = useState<Patient | null>(null);
  const [taskPriorityId, setTaskPriorityId] = useState<string | null>(null);
  const [taskStatusId, setTaskStatusId] = useState<string | null>(null);
  const [subcategory, setSubcategory] = useState<Category | null>(null);

  const [taskEditCounter, setTaskEditCounter] = useState<number>(0);

  const [voidReason, setVoidReason] = useState<string>('');

  const [isDueDateValid, setIsDueDateValid] = useState<boolean>(false);

  const history = useHistory();

  const { isChildPath } = usePath();

  const { data: listOfVoidReasons } = useGetTaskVoidReasonsEnumQuery();

  const { isFamilyTask } = originalTask ?? {};

  const {
    categories,
    editTask,
    editTaskError,
    editTaskStatusCode,
    isEditTaskLoading,
    resetEditTaskError,
    isUnsavedEditTaskChanges,
    setIsUnsavedEditTaskChanges,
  } = useTaskContext();

  const params = useParams<{ patientId?: string }>();
  const { trackEvent } = useAnalytics();

  // GX-3701: remove params[0] fallback once old wildcard routing is retired
  let patientId: string | undefined;
  if (isChildPath(Path.MAUD_PATIENTS)) {
    patientId = params.patientId ?? params[0]?.split('/')[0];
  }
  const backToTasksLocation = useMemo(() => {
    if (isChildPath(Path.MAUD_PATIENTS) && patientId) {
      const tasksBase = `${Path.MAUD_PATIENTS}/${patientId}/${PatientTabsCollection[PatientTabs.TASKS].subPath}`;
      const pathSegments = window.location.pathname.split('/tasks/');
      const afterTasks = pathSegments[1] ?? '';
      const subTabSlug = afterTasks.split('/')[0];
      const knownSubTabs: string[] = Object.values(TasksSubTab);
      const subTabSuffix = knownSubTabs.includes(subTabSlug)
        ? `/${subTabSlug}`
        : '';
      return { pathname: `${tasksBase}${subTabSuffix}` };
    }
    return {
      pathname: Path.MAUD_TASKS,
      state: {
        fromTaskRoute: true,
      },
    };
  }, []);

  const [
    getTasks,
    {
      data: taskData,
      status: taskDataStatus,
      isLoading: isGetTaskByIdDataLoading,
      isError: isGetTaskByIdError,
      isUninitialized: isGetTaskByIdUninitialized,
    },
  ] = useLazyGetTasksQuery();

  const {
    data: familyTaskAssigneeOptions,
    error: familyTaskAssigneeOptionsError,
    isLoading: isFamilyTaskAssigneeOptionsLoading,
  } = useGetAppUsersQuery(
    { includeGeneral: true, includeInactive: true, patientId },
    {
      skip: !patientId || !isFamilyTask,
    },
  );

  const isMinorPatient = (userType: string, dob: string) =>
    userType.toLowerCase() === 'patient' &&
    getAgeFromDOB(dob, DateFormat.shortYearMonthDay) < 18;

  const filteredFamilyTaskAssigneeOptions = familyTaskAssigneeOptions?.filter(
    (fta) => !isMinorPatient(fta.userType, fta.dateOfBirth),
  );

  useEffect(() => {
    if (familyTaskAssigneeOptionsError) {
      logError(new Error(JSON.stringify(familyTaskAssigneeOptionsError)));
      // eslint-disable-next-line no-console
      console.error(familyTaskAssigneeOptionsError);
    }
  }, [familyTaskAssigneeOptionsError]);

  const scrollToTop = () => {
    document.getElementById(`${TOP_LEVEL_ID}__section`)?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  };
  const getTaskDataById = () => {
    getTasks({
      filterBy: {
        ...(patientId && { 'patient-external-id': patientId }),
        'task-external-id': taskId,
      },
    });
    scrollToTop();
  };

  const setFamilyTaskAssigneeIfNeeded = (task: Task) => {
    if (
      task.isFamilyTask &&
      task.familyAssignees &&
      task.familyAssignees.length > 0
    ) {
      setSelectedFamilyTaskAssignees(task.familyAssignees);
    }
  };

  useEffect(() => {
    if (taskDataStatus == QueryStatus.fulfilled) {
      if (taskData?.data?.length > 0) {
        const task = mapCompServTaskToTask(taskData?.data?.[0], categories);
        setSelectedTask(task);
        setOriginalTask(task);
        setAssignee(task.assignee);
        setCategory(task.category);
        setDescription(task.description);
        setDueDate(task.dueDate);
        setName(task.name);
        setPatient(task.patient);
        setTaskPriorityId(task.taskPriorityId);
        setTaskStatusId(task.taskStatusId);
        setSubcategory(task.subcategory);
        setVoidReason(task.voidReasonExternalId);
        setFamilyTaskAssigneeIfNeeded(task);
      }
    }
  }, [taskDataStatus]);

  useEffect(() => {
    if (taskId) {
      const isFromPatientTasksPage =
        backToTasksLocation.pathname.includes('/tasks');

      if (isFromPatientTasksPage) {
        trackEvent(PatientEvents.uniqueTaskTasksTabButtonClicked, {
          Source: AnalyticsSourceValues.TASKS_TAB,
        });
      } else {
        trackEvent(OverviewPageEvents.uniqueTaskOveviewTabButtonClicked, {
          Source: AnalyticsSourceValues.OVERVIEW_TAB,
        });
      }
      getTaskDataById();
    }
  }, [taskId]);

  const isNonEditable = useMemo(
    () => !isOpenTask(originalTask),
    [originalTask?.taskStatusId],
  );

  useEffect(() => {
    const allowedSubCategories = filterInActiveSubCategories(
      filterDisabledSubCategories(category?.subCategoryList ?? []),
    );

    const newSubCategory = (category?.subCategoryList ?? []).some(
      ({ categoryExternalId }) =>
        categoryExternalId === subcategory?.categoryExternalId,
    )
      ? subcategory
      : (allowedSubCategories[0] ?? null);

    setSubcategory(newSubCategory);
  }, [category?.categoryName]);

  // Update task state if update task response is successful
  useEffect(() => {
    if (
      editTaskStatusCode === 200 &&
      (isUnsavedEditTaskChanges || newStatusPlaceholder)
    ) {
      if (isUnsavedEditTaskChanges) {
        setIsUnsavedEditTaskChanges(false);
      } else {
        const newTaskStatusId =
          taskStatusDefinitions[newStatusPlaceholder].value;
        setTaskStatusId(newTaskStatusId);
        setNewStatusPlaceholder(null);
        setIsCompleteTaskModalOpen(false);
        setIsVoidTaskModalOpen(false);
        setIsChangeVoidReasonModalOpen(false);
      }
      getTaskDataById();

      setTaskEditCounter((c) => c + 1);
    }
  }, [editTaskStatusCode]);

  const listOfVoidReasonsWithHeaderText = useMemo(
    () =>
      sortBy(
        (listOfVoidReasons ?? []).map(
          ({ externalVoidReasonId, statusCode, voidReason }: VoidReason) => ({
            headerText: `${statusCode} reasons`,
            node: voidReason,
            value: externalVoidReasonId,
          }),
        ),
        ['headerText', 'node'],
      ),
    [listOfVoidReasons],
  );

  const handleStateFieldChange = <T,>(
    setState: Dispatch<SetStateAction<T>>,
    value: T,
    doNotSet = false,
  ): void => {
    resetEditTaskError();
    if (!doNotSet) {
      setIsUnsavedEditTaskChanges(true);
      setState(value);
    }
  };

  const handleTaskNameChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ): void => {
    handleStateFieldChange(setName, e.target.value);
  };

  const handleTaskDescriptionChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ): void => {
    handleStateFieldChange(setDescription, e.target.value);
  };

  const handleTaskDueDateChange = (date: Date) => {
    let updatedDate = '';

    if (date != null) {
      updatedDate = DateTime.fromJSDate(date).toFormat(DateFormat.longDate);
    }

    handleStateFieldChange(setDueDate, updatedDate, updatedDate === dueDate);
  };

  const handleTaskStatusChange = (newStatus: TaskStatusValue) => {
    resetEditTaskError();
    if (newStatus === TaskStatusValue.DONE) {
      setIsCompleteTaskModalOpen(true);
    } else {
      editTask(originalTask, newStatus);
      setNewStatusPlaceholder(newStatus);
    }
  };

  const handleTaskCategoryChange = (value: string): void => {
    handleStateFieldChange(
      setCategory,
      categories.find(({ categoryName }) => categoryName === value),
      category?.categoryName === value,
    );
  };

  const handleTaskSubCategoryChange = (value: string): void => {
    handleStateFieldChange(
      setSubcategory,
      category.subCategoryList.find(
        ({ categoryName }) => categoryName === value,
      ),
      subcategory?.categoryName === value,
    );
  };

  const handleTaskPriorityChange = (value: string): void => {
    handleStateFieldChange(setTaskPriorityId, value, taskPriorityId === value);
  };

  const handleTaskAssigneeChange = (
    newAssignee: Partial<GetProviderByIdResponse> | undefined,
  ): void => {
    handleStateFieldChange(
      setAssignee,
      (newAssignee ?? null) as GetProviderByIdResponse | null,
      assignee?.userId === newAssignee?.userId,
    );
  };

  const handleFamilyTaskAssigneeChange = (
    newAssignee: FamilyTaskAssignee,
    removed = false,
  ): void => {
    let assigneeList: FamilyTaskAssignee[];
    if (removed) {
      assigneeList = selectedFamilyTaskAssignees.filter(
        ({ externalId }: FamilyTaskAssignee) =>
          externalId !== newAssignee.externalId,
      );
      handleStateFieldChange(
        setSelectedFamilyTaskAssignees,
        assigneeList.length ? assigneeList : null,
        false,
      );
    } else {
      assigneeList =
        selectedFamilyTaskAssignees && selectedFamilyTaskAssignees.length > 0
          ? [...selectedFamilyTaskAssignees, newAssignee]
          : [newAssignee];
      const shouldSet =
        selectedFamilyTaskAssignees &&
        selectedFamilyTaskAssignees.length > 0 &&
        !!selectedFamilyTaskAssignees.find(
          (assignee) => assignee.externalId === newAssignee?.externalId,
        );
      handleStateFieldChange(
        setSelectedFamilyTaskAssignees,
        assigneeList,
        shouldSet,
      );
    }
  };

  const handleTaskPatientChange = (newPatient: Patient): void => {
    handleStateFieldChange(
      setPatient,
      newPatient,
      patient?.userId === newPatient?.userId,
    );
  };

  const handleCancel = (): void => {
    resetEditTaskError();
    setIsUnsavedEditTaskChanges(false);
    setAssignee(originalTask.assignee);
    setCategory(originalTask.category);
    setDescription(originalTask.description);
    setDueDate(originalTask.dueDate);
    setName(originalTask.name);
    setPatient(originalTask.patient);
    setTaskPriorityId(originalTask.taskPriorityId);
    setSubcategory(originalTask.subcategory);
    setFamilyTaskAssigneeIfNeeded(originalTask);
  };

  const handleSave = (newPatient?: Patient): void => {
    const updatedTask: Task = {
      ...originalTask,
      assignee,
      category,
      description,
      dueDate,
      name,
      patient: newPatient ?? patient,
      subcategory,
      taskPriorityId,
      taskStatusId,
    };

    if (isFamilyTask && selectedFamilyTaskAssignees) {
      updatedTask.familyAssignees = selectedFamilyTaskAssignees;
    }

    editTask(updatedTask);
    scrollToTop();
  };

  const { UnsavedChangesModal } = useUnsavedChangesGuard({
    isDirty: isUnsavedEditTaskChanges,
    onDiscard: handleCancel,
  });

  const handleBackClick = (): void => {
    history.push(backToTasksLocation);
  };

  const handleTaskStatusChangeConfirm =
    (status: TaskStatusValue, voidReason?: string) => () => {
      editTask(originalTask, status, voidReason);
      setNewStatusPlaceholder(status);
    };

  const onVoidClickConfirm = () =>
    handleTaskStatusChangeConfirm(
      getVoidReasonData(listOfVoidReasons, voidReason, 'statusCode') ===
        TaskStatusLabel.CLOSED
        ? TaskStatusValue.CLOSED
        : TaskStatusValue.VOID,
      voidReason,
    )();

  const onChangeVoidReasonCancel = (): void => {
    setIsChangeVoidReasonModalOpen(false);
    setTaskStatusId(originalTask.taskStatusId);
    setVoidReason(originalTask.voidReasonExternalId);
  };

  const validateDueDate = (isValid: boolean): void => {
    setIsDueDateValid(!!dueDate && isValid);
  };

  const dontHaveTask =
    isGetTaskByIdError ||
    (!isGetTaskByIdDataLoading && !isGetTaskByIdUninitialized) ? (
      <TaskNotFound handleBackClick={handleBackClick} />
    ) : (
      <Loading />
    );

  const changeVoidReason = (reasonExtId: string): void => {
    setVoidReason(reasonExtId);
    const statusCode = getVoidReasonData(
      listOfVoidReasons,
      reasonExtId,
      'statusCode',
    );
    setTaskStatusId(
      taskStatusDefinitions.find(({ title }) => title === statusCode)?.value,
    );
  };

  const isAssigneeNonEditable = !isOpenTask(originalTask, true);

  return !selectedTask ? (
    dontHaveTask
  ) : (
    <>
      {UnsavedChangesModal}
      <VoidOrCloseTaskModal
        body="Are you sure you want to void or close this task? It's impossible to recover this task."
        id="void-task"
        isDisabled={!voidReason}
        isOpen={isVoidTaskModalOpen}
        menuLabel="Reason"
        onClose={() => {
          setVoidReason('');
          setIsVoidTaskModalOpen(false);
        }}
        onConfirm={onVoidClickConfirm}
        onSelectReason={setVoidReason}
        reasonOptions={listOfVoidReasonsWithHeaderText}
        selectedReason={voidReason}
        title="Void or close"
      />
      <VoidOrCloseTaskModal
        body="You will change the current closed or void reason."
        id="change-void-task-reason"
        isDisabled={
          !voidReason ||
          voidReason === originalTask.voidReasonExternalId ||
          isEditTaskLoading
        }
        isOpen={isChangeVoidReasonModalOpen}
        menuLabel="New reason"
        onClose={onChangeVoidReasonCancel}
        onConfirm={onVoidClickConfirm}
        onSelectReason={changeVoidReason}
        reasonOptions={listOfVoidReasonsWithHeaderText}
        selectedReason={voidReason}
        title="Change reason"
      />
      <ConfirmModal
        body="You will not be able to edit the task once marked as complete."
        cancelLabel="Return to editing"
        confirmLabel="Mark as complete"
        confirmVariant="accent.critical"
        id="complete-task"
        isOpen={isCompleteTaskModalOpen}
        onClose={() => setIsCompleteTaskModalOpen(false)}
        onConfirm={handleTaskStatusChangeConfirm(TaskStatusValue.DONE)}
        title="Complete Task"
      />
      <ChakraBox
        display="grid"
        gridTemplateRows="auto 1fr"
        height="100%"
        id={`${TOP_LEVEL_ID}__section`}
        overflowY="auto"
        paddingRight={PAGE_LAYOUT.rightSpacing}
        paddingTop="24px"
      >
        <HeaderSection
          isEditing={isUnsavedEditTaskChanges}
          isNonEditable={isNonEditable || isFamilyTask}
          onBackClick={handleBackClick}
          onVoidClick={() => setIsVoidTaskModalOpen(true)}
        />
        <ChakraBox display="flex">
          <ChakraBox marginRight="56px" width="100%">
            <TitleSection
              isNonEditable={isNonEditable || isFamilyTask}
              name={name}
              onChange={handleTaskNameChange}
              status={taskStatusId}
            />
            <ActionsSection
              closedReason={getVoidReasonData(
                listOfVoidReasons,
                voidReason,
                'voidReason',
              )}
              currentDueDate={selectedTask.dueDate}
              date={dueDate}
              hasEditFamilyTaskPermission={hasEditFamilyTaskPermission}
              isEditing={isUnsavedEditTaskChanges}
              isFamilyTask={isFamilyTask}
              isNonEditable={isNonEditable}
              onClosedReasonChangeClick={() =>
                setIsChangeVoidReasonModalOpen(true)
              }
              onDateChange={handleTaskDueDateChange}
              onStatusChange={handleTaskStatusChange}
              taskStatusId={taskStatusId}
              validateDueDate={validateDueDate}
            />
            <DetailsSection
              categories={categories}
              category={category}
              description={description}
              isNonEditable={isNonEditable || isFamilyTask}
              onChangeCategory={handleTaskCategoryChange}
              onChangeDescription={handleTaskDescriptionChange}
              onChangePriority={handleTaskPriorityChange}
              onChangeSubCategory={handleTaskSubCategoryChange}
              subcategory={subcategory?.categoryName}
              taskPriorityId={taskPriorityId}
            />
            <ChakraBox borderTop={`2px solid ${neutral.line}`} padding="16px 0">
              <ChakraText
                {...labelBold}
                color="neutral.secondary"
                marginBottom="4px"
              >
                Assignee
              </ChakraText>
              <ChakraBox paddingTop="4px">
                <AssigneeSelection
                  assignee={assignee}
                  hideLabel
                  id={TOP_LEVEL_ID}
                  isNonEditable={
                    isAssigneeNonEditable ||
                    (isFamilyTask && !hasEditFamilyTaskPermission)
                  }
                  updateAssignee={handleTaskAssigneeChange}
                />
              </ChakraBox>
            </ChakraBox>
            {isFamilyTask && (
              <ChakraBox
                borderTop={`2px solid ${neutral.line}`}
                padding="16px 0"
              >
                <ChakraText
                  {...labelBold}
                  color="neutral.secondary"
                  marginBottom="4px"
                >
                  Patient or Support Assignee
                </ChakraText>
                <ChakraBox paddingTop="4px">
                  <FamilyAssigneeSelection
                    assignees={selectedFamilyTaskAssignees}
                    familytaskAssignees={filteredFamilyTaskAssigneeOptions}
                    hideLabel
                    id={TOP_LEVEL_ID}
                    isLoading={isFamilyTaskAssigneeOptionsLoading}
                    isNonEditable={
                      isNonEditable ||
                      (isFamilyTask && !hasEditFamilyTaskPermission)
                    }
                    updateAssignee={handleFamilyTaskAssigneeChange}
                  />
                </ChakraBox>
              </ChakraBox>
            )}
            <ActivitySection
              isNonEditable={isNonEditable || isFamilyTask}
              taskData={taskData?.data?.[0]}
              taskEditCounter={taskEditCounter}
              taskExternalId={selectedTask?.taskExternalId}
            />
          </ChakraBox>
          <ChakraBox width="420px">
            {((originalTask?.taskStatusId ?? '').toString() !==
              taskStatusDefinitions[TaskStatusValue.VOID].value ||
              patient) && (
              <PatientSelection
                hideLabel
                id={TOP_LEVEL_ID}
                isDisabled={isFamilyTask}
                patient={patient}
                showProfileIcon
                updatePatient={handleTaskPatientChange}
              />
            )}
            {/* Patient creation/matching from tasks removed per ticket; use inquiry detail drawer instead. */}
            {isFamilyTask && (
              <ChakraBox
                background="primary.10"
                borderRadius="12px"
                id={`${TOP_LEVEL_ID}__family-task__info-container`}
                marginTop="24px"
                padding="8px"
                width="fit-content"
              >
                <ChakraText
                  {...paragraphBold}
                  color="primary"
                  id={`${TOP_LEVEL_ID}__family-task__info-text`}
                >
                  This is a patient or support task, which has restricted
                  access.
                </ChakraText>
              </ChakraBox>
            )}
          </ChakraBox>
        </ChakraBox>
        <EditTaskConfirmDrawer
          editTaskError={editTaskError}
          handleCancel={handleCancel}
          handleSave={handleSave}
          isSaveButtonDisabled={
            !isValidName(name) ||
            !isDueDateValid ||
            isEditTaskLoading ||
            (!!selectedTask.assignee && !assignee) ||
            (isFamilyTask && !selectedFamilyTaskAssignees)
          }
          isVisible={isUnsavedEditTaskChanges}
        />
      </ChakraBox>
    </>
  );
};

export default TaskDetail;
